/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */


import ArgosFlexbox from "../../Containers/ArgosFlexbox"
import Chip from "@mui/material/Chip"
import {ITreeNode} from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent"
import {selectDatasetsExecutionStatus, selectStudyIdToTreeTypeToTree} from "../../../../redux/ducks/CoverageRequirements/slice"
import Tooltip from "@mui/material/Tooltip"
import {useAppSelector} from "../../../../redux/hooks"
import React, {useState} from "react"
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import CheckboxDraggableTree from "../../Tree/CheckboxDraggableTree";
import {SINGLE_DATASET_TREE_TYPE} from "../../../pages/CoverageRequirements/ItemDetails/constants";
import {useDeepCompareEffect} from "../../../../hooks/useDeepCompareEffect/useDeepCompareEffect";


interface EditMergeSetSourceDatasetsProps {
  mergeSetData: any
  onChange: (data: any) => void
}

const EditMergeSetSourceDatasets = (props: EditMergeSetSourceDatasetsProps) => {
  const [originalSelectedDatasets, setOriginalSelectedDatasets] = useState<string[]>([])
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])

  // get study id from url
  const {studyId} = useGetUrlParams()

  const [initialSelectedNodes, setInitialSelectedNodes] = useState<string[]>([])

  const rawRedlineTreeDbItem = useAppSelector(state => selectStudyIdToTreeTypeToTree(state, SINGLE_DATASET_TREE_TYPE, studyId))
  const executionStatusMap: any = useAppSelector(selectDatasetsExecutionStatus)

  useDeepCompareEffect(() => {
    if (props.mergeSetData.selectedDatasetIds && props?.mergeSetData?.originalSelectedDatasetIds && rawRedlineTreeDbItem) {
      const [nodeIds, datasetIds] = findNodeIdsByDatasetIds(props.mergeSetData.selectedDatasetIds, rawRedlineTreeDbItem.tree)
      if (initialSelectedNodes.length === 0) {
        setInitialSelectedNodes(nodeIds)
      }
      setControlledCheckedNodes(nodeIds)
      setSelectedDatasets(datasetIds)
      setOriginalSelectedDatasets(props?.mergeSetData?.originalSelectedDatasetIds)
    }
  }, [props?.mergeSetData?.originalSelectedDatasetIds, rawRedlineTreeDbItem, executionStatusMap], [0, 1])

  const handleSelectChange = (event: any, nodes: any) => {
    const nodeIds: string[] = []
    const datasetIds: string[] = []

    for (let node of nodes) {
      /** Allowing empty folders to not prevent the parent folder from being unchecked */
      nodeIds.push(node.nodeId)
      if (node.nodeType === "dataset" && node.datasetId)     
        datasetIds.push(node.datasetId)
    }

    setSelectedDatasets(datasetIds)
    setControlledCheckedNodes(nodeIds)

    props.onChange({
      ...props?.mergeSetData,
      "selectedDatasetIds": datasetIds
    })
  }

  const findNodeIdsByDatasetIds = (datasetIds: string[], treeData: ITreeNode) => {
    const nodeIds: string[] = []
    const filteredDatasetIds: string[] = []

    const traverseTree = (node: ITreeNode) => {
      if(node.nodeId && node.datasetId && datasetIds.includes(node.datasetId))
        nodeIds.push(node.nodeId)
        filteredDatasetIds.push(node.datasetId ?? '')
      if (Array.isArray(node.children)) {
        node.children.forEach((childNode: ITreeNode) => {
          traverseTree(childNode)
        })
      }
    }

    traverseTree(treeData)

    return [nodeIds, filteredDatasetIds]
  }

  const renderTreeItemLabel = (nodes: ITreeNode) => {
    const isDataset = nodes.nodeType == "dataset"
    const nodeId = nodes.nodeId
    let labelContent = (
      <ArgosFlexbox style={{alignItems: 'center',}}>
        {isDataset && nodes.datasetId && originalSelectedDatasets.includes(nodes.datasetId) && <Tooltip title="This data set was included in the original merge set">
          <Chip label={"Original"}/>
        </Tooltip>}
        <span>
          {nodes.name}
        </span>
      </ArgosFlexbox>
    )
    return (
      <ArgosFlexbox style={{height: 40, alignItems: "center"}}>
        {/*<ArgosDatasetIcon/>*/}
        {labelContent}
      </ArgosFlexbox>
    )
  }

  const [controlledCheckedNodes, setControlledCheckedNodes] = useState<string[]>([])
  return (
    <div style={{padding: 5, alignSelf: 'center', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', alignItems: 'flex-start', gap: 30, height: 480}}>
      <div>{props.mergeSetData?.selectedDatasetIds?.length} Selected</div>
      {rawRedlineTreeDbItem &&
        <>
          <CheckboxDraggableTree
            treeType={'Single'}
            treeData={rawRedlineTreeDbItem}
            onChangeCheckedNodes={(_nodes) => {
              handleSelectChange(null, _nodes)
            }}
            renderTreeItemLabel={renderTreeItemLabel}
            controlledCheckedNodes={controlledCheckedNodes}
            setControlledCheckedNodes={setControlledCheckedNodes}
            isMergeDialogUp={false}
            checkIfMerging={() => false}
            isDeleting={false}
          />
        </>
      }
    </div>
  )
}

export default EditMergeSetSourceDatasets
